/**
 * The two ketua roles (spec 2026-09-30, 7): a ketua lingkungan over one lingkungan, a ketua
 * stasi with the same rights over every lingkungan under the stasi. Screens hide the
 * Lingkungan filter for a single lingkungan only, and the Stasi filter for both.
 */
import type { Peran } from './schema/contract'

export const PERAN_KETUA: readonly Peran[] = ['ketua_lingkungan', 'ketua_stasi']

export function adalahKetua(peran: string | null | undefined): boolean {
  return PERAN_KETUA.some((p) => p === peran)
}

/** One lingkungan only: the Lingkungan filter is not offered. */
export function satuLingkungan(peran: string | null | undefined): boolean {
  return peran === 'ketua_lingkungan'
}

/**
 * The access ruling of 2026-10-03 (plan 10): the parish's data (Keluarga, Umat and a person,
 * Tambah umat, Pendaftaran, the books, Surat, Mutasi, Impor) serve the parish roles and both
 * ketua; with `tanpaKetua` (Mutasi, Impor) the parish staff only. Never a diocese role: an admin
 * keuskupan working in a parish arrives here as that parish's admin paroki. A Pastor Paroki
 * (release 32) reads the parish's data, but not Mutasi or Impor, which are the staff's work; what
 * he may change is `bolehUbahDataParoki`'s.
 */
export function bolehDataParoki(peran: string | null | undefined, tanpaKetua = false): boolean {
  if (peran === null || peran === undefined) return false
  if (peran === 'admin_keuskupan' || peran === 'uskup' || peran === 'dekan') return false
  if (tanpaKetua && peran === 'pastor_paroki') return false
  return !(tanpaKetua && adalahKetua(peran))
}

/**
 * Release 32 (owner, 2026-11-02): the parish roles that read the parish and change none of its
 * records: the Pastor Paroki, who issues letters only (`bolehTerbitkanSurat`).
 */
export const PERAN_BACA_PAROKI: readonly Peran[] = ['pastor_paroki']

/** Reads the parish's data and changes none of it (`PERAN_BACA_PAROKI`). */
export function hanyaBacaParoki(peran: string | null | undefined): boolean {
  return PERAN_BACA_PAROKI.some((p) => p === peran)
}

/**
 * May the role change the parish's data where `bolehDataParoki` lets it read them (Tambah umat,
 * Pendaftaran, a book entry, a person's or a family's edits, Upacara, Hapus)? Every role that reads
 * them but a Pastor Paroki.
 */
export function bolehUbahDataParoki(peran: string | null | undefined, tanpaKetua = false): boolean {
  return bolehDataParoki(peran, tanpaKetua) && !hanyaBacaParoki(peran)
}

/**
 * The roles that issue letters (Cetak surat, Cetak baru, Terbitkan ulang, Cetak dalam format
 * baru, a batch): the parish staff (an admin keuskupan working in a parish is its admin paroki
 * there) and, from release 32, the Pastor Paroki. Never a ketua, never a diocese view.
 */
export function bolehTerbitkanSurat(peran: string | null | undefined): boolean {
  return bolehDataParoki(peran) && !adalahKetua(peran)
}

/**
 * The roles that see and write a person's sensitive fields (`umat_profil`: blood type, ethnicity,
 * income band, health insurance; owner, 2026-10-08): Admin and Staf Paroki and the admin
 * keuskupan. A ketua sees the person without them, and the read-only diocese roles (uskup, dekan),
 * which the owner did not name, neither receive nor see them. The sync rules
 * (../sync/visibility) and every screen, print and download ask this one list.
 *
 * Release 32 (owner, 2026-11-02): the Pastor Paroki sees them too, for his own parish only: a
 * pastor who is also Ketua Wilayah receives none of the wilayah's other parishes
 * (`sensitifHanya`, ../sync/visibility). He writes none (step 1 of the push).
 */
export const PERAN_DATA_SENSITIF: readonly Peran[] = [
  'admin_paroki',
  'staf_paroki',
  'pastor_paroki',
  'admin_keuskupan',
]

export function bolehDataSensitif(peran: string | null | undefined): boolean {
  return PERAN_DATA_SENSITIF.some((p) => p === peran)
}

/**
 * Peta umat (release 20; design: "Admin/Staf Paroki their parish; Ketua Lingkungan/Stasi their
 * own area; diocese admins everything"): the roles that see the map. Ruling 2: the uskup and a
 * Ketua Wilayah (`dekan`), whom the design does not name, have none: no shelf item, the route
 * refused, the worker's read refused.
 */
export const PERAN_PETA: readonly Peran[] = [
  'admin_paroki',
  'staf_paroki',
  // Release 32: his own parish's map; a wilayah's other parishes have none, as for a dekan.
  'pastor_paroki',
  'ketua_lingkungan',
  'ketua_stasi',
  'admin_keuskupan',
]

export function bolehLihatPeta(peran: string | null | undefined): boolean {
  return PERAN_PETA.some((p) => p === peran)
}
